import React, { useMemo, useState } from "react";
import { calcNPS, formatINR } from "../../lib/finance";
import { Slider, InputRow, ResultRow, SplitBar, Pills, RightPanel, Divider, HeroValue } from "./CalcUI";

const SECTORS = { "All Citizen / Corporate": "nongov", "Government Employee": "gov" };

const pct = (part, whole) => (whole > 0 ? Math.round((part / whole) * 100) : 0);

export const NPSTab = () => {
  const [sectorLabel, setSectorLabel] = useState("All Citizen / Corporate");
  const sector = SECTORS[sectorLabel];
  const [monthly, setMonthly] = useState(5000);
  const [returnPct, setReturnPct] = useState(10);
  const [currentAge, setCurrentAge] = useState(30);
  const [retirementAge, setRetirementAge] = useState(60);
  const [annuityRate, setAnnuityRate] = useState(6);

  const result = useMemo(
    () => calcNPS(monthly, returnPct, currentAge, retirementAge, annuityRate, sector),
    [monthly, returnPct, currentAge, retirementAge, annuityRate, sector]
  );
  const animKey = `nps-${Math.round(result.corpus)}-${monthly}-${returnPct}-${currentAge}-${retirementAge}-${annuityRate}-${sector}`;

  const balanceLabel =
    result.balanceKind === "sur"
      ? "Balance (SUR or annuity)"
      : `Annuity (min ${pct(result.annuityCorpus, result.corpus)}%)`;

  let exitNote;
  if (sector === "gov") {
    exitNote = "Government subscribers: at least 40% of the corpus must buy an annuity; up to 60% can be taken as a lump sum.";
  } else if (result.balanceKind === "none") {
    exitNote = "Corpus of Rs.8 lakh or less: the full amount can be withdrawn as a lump sum or systematically; no annuity is required.";
  } else if (result.balanceKind === "sur") {
    exitNote = "Corpus between Rs.8 lakh and Rs.12 lakh: up to Rs.6 lakh as a lump sum; the balance via systematic unit redemption (over at least 6 years) or an annuity.";
  } else {
    exitNote = "Corpus above Rs.12 lakh: up to 80% as a lump sum (or systematic withdrawals); at least 20% must buy an annuity.";
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2">
      <div className="p-6 sm:p-10">
        <div className="mb-7">
          <div className="mb-3" style={{ color: "#4A5568", fontSize: 13, fontWeight: 500 }}>Subscriber Type</div>
          <Pills items={Object.keys(SECTORS)} value={sectorLabel} onChange={setSectorLabel} prefix="nps-sector" />
        </div>

        <InputRow
          label="Monthly Contribution"
          valueText={`Rs. ${formatINR(monthly)}`}
          valueTestId="nps-monthly-value"
          finePrint={<><span>Rs.500</span><span>Rs.50,000</span></>}
        >
          <Slider value={monthly} min={500} max={50000} step={500} onChange={setMonthly} ariaLabel="NPS monthly" />
        </InputRow>

        <InputRow
          label="Expected Annual Return"
          valueText={`${returnPct.toFixed(1)}% per annum`}
          valueTestId="nps-return-value"
          finePrint={<><span>5%</span><span>15% per annum</span></>}
        >
          <Slider value={returnPct} min={5} max={15} step={0.1} onChange={setReturnPct} ariaLabel="NPS return" />
        </InputRow>

        <InputRow
          label="Current Age"
          valueText={`${currentAge} ${currentAge === 1 ? "Year" : "Years"}`}
          valueTestId="nps-current-age-value"
          finePrint={<><span>18</span><span>59</span></>}
        >
          <Slider value={currentAge} min={18} max={59} step={1} onChange={setCurrentAge} ariaLabel="Current age" />
        </InputRow>

        <InputRow
          label="Retirement Age"
          valueText={`${retirementAge} ${retirementAge === 1 ? "Year" : "Years"}`}
          valueTestId="nps-retirement-age-value"
          finePrint={<><span>{Math.max(currentAge + 1, 60)}</span><span>75</span></>}
        >
          <Slider
            value={retirementAge}
            min={Math.max(currentAge + 1, 60)}
            max={75}
            step={1}
            onChange={setRetirementAge}
            ariaLabel="Retirement age"
          />
        </InputRow>

        <InputRow
          label="Annuity Rate (Post Retirement)"
          valueText={`${annuityRate.toFixed(2)}% per annum`}
          valueTestId="nps-annuity-rate-value"
          finePrint={<><span>4%</span><span>10%</span></>}
        >
          <Slider value={annuityRate} min={4} max={10} step={0.1} onChange={setAnnuityRate} ariaLabel="Annuity rate" />
        </InputRow>

        <p style={{ color: "#718096", fontSize: 11, lineHeight: 1.5 }} data-testid="nps-exit-note">
          {exitNote} Based on PFRDA exit rules notified in December 2025 (normal exit at 60, or after 15 years
          in NPS). Only up to 60% of the corpus is tax-free under current income-tax rules; any lump sum
          beyond that may be taxable. Projections are illustrative and not guaranteed.
        </p>
      </div>

      <RightPanel testId="nps-results">
        <HeroValue
          label="Total Retirement Corpus"
          value={`Rs. ${formatINR(result.corpus)}`}
          animKey={animKey}
          testId="nps-corpus-value"
        />
        <Divider />
        <div className="space-y-3">
          <ResultRow label="Total Invested" value={`Rs. ${formatINR(result.invested)}`} testId="nps-row-invested" />
          <ResultRow label="Est. Returns" value={`Rs. ${formatINR(result.gains)}`} valueColor="#1D9E75" testId="nps-row-gains" />
          <ResultRow
            label={`Max Lump Sum (${pct(result.lumpsum, result.corpus)}%)`}
            value={`Rs. ${formatINR(result.lumpsum)}`}
            testId="nps-row-lumpsum"
          />
          {result.balanceKind !== "none" && (
            <ResultRow label={balanceLabel} value={`Rs. ${formatINR(result.annuityCorpus)}`} testId="nps-row-annuity" />
          )}
          <ResultRow
            label={result.balanceKind === "none" ? "Est. Monthly Pension" : "Est. Monthly Pension (if annuitised)"}
            value={result.balanceKind === "none" ? "No annuity required" : `Rs. ${formatINR(result.monthlyPension)}`}
            valueColor="#F4A700"
            testId="nps-row-pension"
          />
        </div>
        <Divider />
        <SplitBar
          leftPct={result.investedPct}
          rightPct={result.gainsPct}
          leftLabel="Invested"
          rightLabel="Returns"
          leftColor="#F4A700"
          rightColor="#1D9E75"
          testId="nps-split-bar"
        />
      </RightPanel>
    </div>
  );
};
